import { Link } from "react-router-dom";

import Avatar from "../components/Avatar.jsx";
import Donut from "../components/Donut.jsx";
import PieLegend from "../components/PieLegend.jsx";
import FollowButton from "../components/FollowButton.jsx";
import { useSession } from "../context/SessionContext.jsx";
import { useAllUsers } from "../hooks/useUsers";
import {
  formatDate,
  formatMoney,
  formatReturn,
  greeting,
  mostHeld,
  pieLabel,
  returnClass,
} from "../utils/portfolio";
import "./HomePage.css";

// Dashboard: My portfolio, people I follow, top performers, most-held funds
const HomePage = () => {
  const { currentUid, following } = useSession();
  const { users, loading } = useAllUsers();

  if (loading) return (
    <main className="page">
      <p className="muted">Loading…</p>
    </main>
  );

  const me = users.find((u) => u.uid === currentUid);
  const others = users.filter((u) => u.uid !== currentUid);
  const followed = others.filter((u) => following.has(u.uid));
  const topPerformers = [...others].sort((a, b) => (
    (b.portfolio?.return1y ?? 0) - (a.portfolio?.return1y ?? 0)
  ));
  const popular = mostHeld(users, currentUid);

  return (
    <main className="page">
      <div className="home__intro">
        <h1 className="page-title">
          {greeting()}, {me ? me.name.split(" ")[0] : "there"}
        </h1>
        <p className="muted">
          Real women. Real portfolios. Here's what's moved in your circle.
        </p>
      </div>

      <div className="home__row">
        {me && <MyPortfolioCard user={me} />}

        <section className="card home__following" aria-labelledby="following-title">
          <div className="home__head">
            <h2 id="following-title" className="section-title">People you follow</h2>
            <span className="small muted">{followed.length} following</span>
          </div>
          {followed.length === 0 ? (
            <p className="home__empty">
              You're not following anyone yet. Follow a top performer below to
              see their moves here.
            </p>
          ) : (
            <ul className="home__list">
              {followed.map((u) => <FollowedRow key={u.uid} user={u} />)}
            </ul>
          )}
        </section>
      </div>

      <div className="home__row home__row--top">
        <section className="card home__top" aria-labelledby="top-title">
          <div className="home__head">
            <h2 id="top-title" className="section-title">Top performers</h2>
            <span className="small muted">Ranked by 1-year return</span>
          </div>
          <ol className="home__list">
            {topPerformers.map((u, i) => (
              <TopPerformerRow key={u.uid} user={u} rank={i + 1} />
            ))}
          </ol>
          <p className="home__note">
            Past returns aren't a guide to the future. Members enter their own
            figures.
          </p>
        </section>

        <section className="card home__popular" aria-labelledby="popular-title">
          <div>
            <h2 id="popular-title" className="section-title">
              Held by several of us
            </h2>
            <p className="small muted">
              The funds that come up again and again across members' pies. A
              starting point for your own research.
            </p>
          </div>
          <ul className="popular">
            {popular.map((p) => (
              <li key={p.key} className="popular__item">
                <div className="popular__top">
                  <span><b>{p.key}</b> <span className="ticker">{p.tickers}</span></span>
                  <b className="small">{p.count} {p.count === 1 ? "member" : "members"}</b>
                </div>
                <div className="popular__bar" aria-hidden="true">
                  <div className="popular__fill" style={{ width: `${p.share}%` }} />
                </div>
                <span className="popular__names">{p.names}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}

const MyPortfolioCard = ({ user }) => {
  const p = user.portfolio ?? {};
  
  return (
    <section className="card home__mine" aria-labelledby="mine-title">
      <div className="home__head">
        <h2 id="mine-title" className="section-title">My portfolio</h2>
        {p.updatedAt && <span className="small muted">Updated {formatDate(p.updatedAt)}</span>}
      </div>
      <div className="home__pie">
        <Donut holdings={p.holdings} size={132} ring={20} label={pieLabel("My", p.holdings)}>
          <span className="donut__big">{p.holdings.length}</span>
          <span className="donut__small">holdings</span>
        </Donut>
        <PieLegend holdings={p.holdings} />
      </div>
      <div className="home__stats tnum">
        {p.showValue && (
          <div>
            <div className="home__stat">{formatMoney(p.value)}</div>
            <div className="small muted">Portfolio value</div>
          </div>
        )}
        <div>
          <div className={`home__stat ${returnClass(p.return1y)}`}>
            {formatReturn(p.return1y)}
          </div>
          <div className="small muted">1-year return</div>
        </div>
        <div>
          <div className="home__stat">{user.followerCount}</div>
          <div className="small muted">Followers</div>
        </div>
      </div>
      <div className="home__actions">
        <Link to="/me/portfolio" className="btn btn--primary">
          Update portfolio
        </Link>
        <Link to={`/profile/${user.uid}`} className="btn btn--ghost">
          View my profile
        </Link>
      </div>
    </section>
  );
};

const FollowedRow = ({ user }) => {
  const p = user.portfolio ?? {};
  const hasPie = (p.holdings || []).length > 0;
  const updated = formatDate(p.updatedAt);

  return (
    <li className="home__list-item">
      <Link to={`/profile/${user.uid}`} className="person-row">
        <Avatar user={user} size={44} />
        <span className="person-row__text">
          <span><b>{user.name}</b> <span className="small muted">· {user.location}</span></span>
          <span className="person-row__line">{user.tagline}</span>
          <span className="person-row__meta">
            {hasPie
              ? (updated ? `Portfolio updated ${updated}` : "Portfolio shared")
              : "Portfolio coming soon"
            }
          </span>
        </span>
        <Donut
          holdings={p.holdings}
          size={40}
          ring={0}
          label={pieLabel(user.name, p.holdings)}
        />
        <b className={`person-row__ret tnum ${returnClass(p.return1y)}`}>
          {formatReturn(p.return1y)}
        </b>
      </Link>
    </li>
  );
};

const TopPerformerRow = ({ user, rank }) => {
  const p = user?.portfolio ?? {};

  return (
    <li className="home__list-item top-row">
      <span className="top-row__rank tnum">{rank}</span>
      <Link to={`/profile/${user.uid}`} className="top-row__who">
        <Avatar user={user} size={44} />
        <span className="person-row__text">
          <b>{user?.name}</b>
          <span className="small muted">
            {user?.location}{user?.investingSince && ` · investing since ${user?.investingSince}`}
          </span>
        </span>
      </Link>
      <span className="top-row__nums tnum">
        <b className={returnClass(p?.return1y)}>{formatReturn(p?.return1y)}</b>
        {p?.showValue && (
          <span className="small muted">{formatMoney(p?.value)}</span>
        )}
      </span>
      <FollowButton uid={user.uid} small />
    </li>
  );
};

export default HomePage;
